import type { StudioAiCostReport } from '../studio/domain/studioTypes'
import type { OperationReceipt } from '../types/simulation'

const loggedReports = new Set<string>()

const usd = (microUsd: number) => `$${(Math.max(0, microUsd) / 1_000_000).toFixed(8)}`

export const logStudioAiCost = (report: StudioAiCostReport | undefined, label: string) => {
  if (!report || loggedReports.has(report.operationId)) return
  loggedReports.add(report.operationId)

  console.groupCollapsed(
    `%c[Voxnela AI Cost]%c ${label} · ${usd(report.totalCostMicroUsd)}`,
    'color:#fff;background:#e5483f;padding:3px 7px;border-radius:7px;font-weight:800',
    'color:inherit;font-weight:700',
  )

  report.lines.forEach((line) => {
    const usage = line.usage
    console.groupCollapsed(`${line.operation} · ${line.model} · ${usd(line.costMicroUsd)}`)
    console.table({
      provider: line.provider,
      model: line.model,
      input: usage?.inputTokens ?? 0,
      textInput: usage?.textInputTokens ?? 0,
      imageInput: usage?.imageInputTokens ?? 0,
      cachedInput: usage?.cachedInputTokens ?? 0,
      cacheWrite: usage?.cacheWriteTokens ?? 0,
      output: usage?.outputTokens ?? 0,
      thinkingReasoning: usage?.thinkingTokens ?? 0,
      tool: usage?.toolTokens ?? 0,
      total: usage?.totalTokens ?? 0,
      costUsd: usd(line.costMicroUsd),
      pricingVersion: line.pricingVersion,
      pricingSource: line.pricingSource,
    })
    console.groupEnd()
  })

  const providerLabels: Record<StudioAiCostReport['lines'][number]['provider'], string> = {
    openai: 'OpenAI API',
    anthropic: 'Anthropic · Claude Haiku',
    'vertex-ai': 'Google Cloud · Vertex AI',
    'google-search': 'Google Cloud · Search grounding',
  }
  const providerTotals = report.lines.reduce<Record<string, number>>((totals, line) => {
    totals[providerLabels[line.provider]] = (totals[providerLabels[line.provider]] ?? 0) + line.costMicroUsd
    return totals
  }, {})

  console.table({
    operationId: report.operationId,
    aiCalls: report.lines.length,
    totalCostUsd: usd(report.totalCostMicroUsd),
    ...Object.fromEntries(Object.entries(providerTotals).map(([provider, total]) => [`${provider} USD`, usd(total)])),
    'Google Cloud infrastructure (Functions/Storage/egress)': 'not included in this operation estimate',
  })
  console.debug('AI cost receipt', report)
  console.groupEnd()
}

export const logOperationReceipt = (receipt: OperationReceipt) => {
  const receiptKey = `operation:${receipt.id}`
  if (loggedReports.has(receiptKey)) return
  loggedReports.add(receiptKey)

  const providerCall = receipt.pricingVersion !== 'local-pilot-no-provider-call'
  const cost = receipt.costMicroUsd === undefined
    ? 'unknown (provider usage missing)'
    : usd(receipt.costMicroUsd)

  console.groupCollapsed(
    `%c[Voxnela AI Cost]%c ${receipt.operation} · ${receipt.model} · ${cost}`,
    'color:#fff;background:#e5483f;padding:3px 7px;border-radius:7px;font-weight:800',
    'color:inherit;font-weight:700',
  )
  console.table({
    operation: receipt.operation,
    model: receipt.model,
    status: receipt.status,
    providerCall,
    costUsd: cost,
    durationMs: receipt.durationMs,
    retry: receipt.retryCount,
    pricingVersion: receipt.pricingVersion ?? 'missing',
    reconciliation: receipt.reconciliationStatus ?? (receipt.reconciled ? 'reconciled' : 'pending'),
  })
  if (receipt.usage) {
    const usage = receipt.usage
    console.table({
      input: usage.inputTokens,
      cachedInput: usage.cachedInputTokens,
      cacheWrite: usage.cacheWriteTokens ?? 0,
      output: usage.outputTokens,
      thinkingReasoning: usage.thinkingTokens,
      billedOutputTotal: usage.outputTokens + usage.thinkingTokens,
      tool: usage.toolTokens,
      total: usage.totalTokens,
    })
  } else {
    console.info(providerCall
      ? 'Provider usage metadata was not returned; cost is not reported as a false $0.'
      : 'Local preview: no provider call and no AI cost.')
  }
  console.debug('AI operation receipt', receipt)
  console.groupEnd()
}
